import { DateTime, Duration } from "luxon"
import { useMemo } from "react"
import { parseDateTimeFromApi } from "../../lib/time"
import { useAppSelector } from "../store/storeHooks"
import { selectSelectedLocale } from "./localizationSelectors"
import { useAppLocalization } from "./components/AppLocalizationProvider"

const ISO_TIME_MINUTE_FORMAT = "HH:mm"
const API_DATETIME_FORMAT = "yyyy-MM-dd HH:mm"
const ISO_DATETIME_MINUTE_FORMAT = "yyyy-MM-dd'T'HH:mm"
const ISO_DATETIME_FORMAT = "yyyy-MM-dd"

function capitalize(text: string) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

interface DateTimeFactoryArguments {
    locale: string
    dateFormat: string
    dayAndDateFormat: string
    timeFormat: string
    timeFormatHms: string
    dateTimeFormat: string
    durationHourMinuteFormat: string
    durationMinuteFormat: string
    durationSecondsFormat: string
    compactPeriod: string
    hourCycle: string
}

type HourCycle = "h11" | "h12" | "h23" | "h24" | undefined

function isHourCycle(value: string | undefined): value is HourCycle {
    switch (value) {
        case "h11":
        case "h12":
        case "h23":
        case "h24":
            return true
        default:
            return false
    }
}

export class DateTimeFactory {
    private locale: string
    private dateFormat: string
    private dayAndDateFormat: string
    private timeFormat: string
    private timeFormatHms: string
    private dateTimeFormat: string
    private durationHourMinuteFormat: string
    private durationMinuteFormat: string
    private durationSecondsFormat: string
    private compactPeriod: string
    private hourCycle: HourCycle

    constructor({
        locale,
        dateFormat,
        dayAndDateFormat,
        timeFormat,
        timeFormatHms,
        dateTimeFormat,
        durationHourMinuteFormat,
        durationSecondsFormat,
        durationMinuteFormat,
        compactPeriod,
        hourCycle,
    }: DateTimeFactoryArguments) {
        this.locale = locale
        this.dateFormat = dateFormat
        this.dayAndDateFormat = dayAndDateFormat
        this.timeFormat = timeFormat
        this.timeFormatHms = timeFormatHms
        this.dateTimeFormat = dateTimeFormat
        this.durationHourMinuteFormat = durationHourMinuteFormat
        this.durationMinuteFormat = durationMinuteFormat
        this.durationSecondsFormat = durationSecondsFormat
        this.compactPeriod = compactPeriod
        this.hourCycle = isHourCycle(hourCycle) ? hourCycle : "h12"
    }

    now() {
        return DateTime.local().setLocale(this.locale)
    }

    fromISO(dateTime: string) {
        return DateTime.fromISO(dateTime, { locale: this.locale })
    }

    fromMillis(value: number) {
        return DateTime.fromMillis(value, { locale: this.locale })
    }

    fromApi(dateTime: string) {
        return parseDateTimeFromApi(dateTime, this.locale)
    }

    fromApiOrUndefined(dateTime: string | null | undefined) {
        return dateTime ? parseDateTimeFromApi(dateTime, this.locale) : undefined
    }

    fromHourMinute(time: string) {
        return DateTime.fromFormat(time, ISO_TIME_MINUTE_FORMAT, { locale: this.locale })
    }

    toHourMinute(dateTime: DateTime) {
        return dateTime.toFormat(ISO_TIME_MINUTE_FORMAT)
    }

    toApi(dateTime: DateTime) {
        return dateTime.toFormat(API_DATETIME_FORMAT)
    }

    toDate(dateTime: DateTime) {
        return dateTime.toFormat(ISO_DATETIME_FORMAT)
    }

    toApiISOMinutesFormat(dateTime: DateTime) {
        return dateTime.toFormat(ISO_DATETIME_MINUTE_FORMAT)
    }

    formatDate(dateTime: DateTime) {
        return dateTime.toFormat(this.dateFormat)
    }

    formatDayAndDate(dateTime: DateTime) {
        return capitalize(dateTime.toFormat(this.dayAndDateFormat))
    }

    formatTime(dateTime: DateTime) {
        return dateTime.toFormat(this.timeFormat)
    }

    formatTimeHms(dateTime: DateTime) {
        return dateTime.toFormat(this.timeFormatHms)
    }

    formatDateTime(dateTime: DateTime) {
        return dateTime.toFormat(this.dateTimeFormat)
    }

    formatDateTimeSafe(dateTime: DateTime | undefined) {
        const res = dateTime && dateTime.isValid ? dateTime.toFormat(this.dateTimeFormat) : undefined
        return res
    }

    formatCustom(dateTime: DateTime, format: string) {
        return dateTime.toFormat(format)
    }

    // handling special ISO duration format PT1H35M
    fromDurationString(time: string) {
        return Duration.fromISO(time)
    }

    formatDuration(duration: Duration) {
        if (duration.hours > 0) {
            return duration.toFormat(this.durationHourMinuteFormat)
        } else if (duration.minutes > 0) {
            return duration.toFormat(this.durationMinuteFormat)
        } else {
            return duration.toFormat(this.durationSecondsFormat)
        }
    }

    formatPeriod(start: DateTime, end: DateTime) {
        if (start.hasSame(end, "day")) {
            return `${this.formatDate(start)} ${this.formatTime(start)} - ${this.formatTime(end)}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }

    getHourCycle() {
        return this.hourCycle
    }

    getAmPm = () => {
        const loc = new Intl.DateTimeFormat(this.locale, { hour: "numeric", hourCycle: this.hourCycle })
        return loc.resolvedOptions().hour12 ?? false
    }

    formatCompactPeriod(startDate: string, endDate: string) {
        const start = this.fromApi(startDate)
        const end = this.fromApi(endDate)
        if (start.hasSame(end, "day")) {
            return `${start.toFormat(this.compactPeriod)} - ${this.formatTime(end)}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }
    formatCompactPeriodFromDateTime(start: DateTime, end: DateTime) {
        if (start.hasSame(end, "day")) {
            const date = start.toFormat("dd/LL")
            const startTime = this.formatTime(start)
            const endTime = this.formatTime(end)

            return `${date} ${startTime} - ${endTime}`
        } else {
            return `${this.formatDateTime(start)} - ${this.formatDateTime(end)}`
        }
    }
}

export function useDateTime() {
    const selectedLocale = useAppSelector(selectSelectedLocale)
    const { l10n } = useAppLocalization()
    const factory = useMemo(() => {
        // gets translations
        return new DateTimeFactory({
            locale: selectedLocale,
            dateFormat: l10n.getString("date-time-format-date"),
            dayAndDateFormat: l10n.getString("date-time-format-day-and-date"),
            timeFormat: l10n.getString("date-time-format-time"),
            timeFormatHms: l10n.getString("date-time-format-time-hms"),
            dateTimeFormat: l10n.getString("date-time-format-date-and-time"),
            durationHourMinuteFormat: l10n.getString("date-time-format-duration-hours-minutes"),
            durationMinuteFormat: l10n.getString("date-time-format-duration-minutes"),
            durationSecondsFormat: l10n.getString("date-time-format-duration-seconds"),
            hourCycle: l10n.getString("date-time-hour-cycle"),
            compactPeriod: l10n.getString("date-time-compact-period-start"),
        })
    }, [selectedLocale, l10n])

    return factory
}
